/**
 * Returns the ISO calendar date (YYYY-MM-DD) for a unix-ms timestamp, evaluated
 * in the given IANA timezone. Uses `en-CA` locale because that locale's default
 * `toLocaleDateString` shape is already `YYYY-MM-DD`, so we don't have to
 * re-parse the parts.
 *
 * @param {number} timestamp - Unix epoch in milliseconds.
 * @param {string} timezone - IANA timezone identifier (e.g. `America/Toronto`).
 * @returns {string} ISO date in the given timezone.
 */
const getIsoDayInTimezone = (timestamp: number, timezone: string): string => {
  // eslint-disable-next-line new-cap
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return formatter.format(new Date(timestamp))
}

/**
 * Formats a unix-ms timestamp as a human time string in a specific timezone.
 * Example output: `9:30 AM` / `10:00 PM`.
 *
 * @param {number} timestamp - Unix epoch in milliseconds.
 * @param {string} timezone - IANA timezone.
 * @returns {string} Localised time string.
 */
const formatTimeInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  // eslint-disable-next-line new-cap
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return formatter.format(new Date(timestamp))
}

/**
 * Formats a unix-ms timestamp as a long human date with weekday, in a specific
 * timezone. Example output: `Wednesday, May 22, 2026`.
 *
 * @param {number} timestamp - Unix epoch in milliseconds.
 * @param {string} timezone - IANA timezone.
 * @returns {string} Localised long date string.
 */
const formatLongDateInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  // eslint-disable-next-line new-cap
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  return formatter.format(new Date(timestamp))
}

/**
 * Returns a compact day label for the calendar header, e.g. `Wed, May 22`.
 *
 * @param {number} timestamp - Unix epoch in milliseconds.
 * @param {string} timezone - IANA timezone.
 * @returns {string} Compact date string.
 */
const formatShortDateInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  // eslint-disable-next-line new-cap
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  return formatter.format(new Date(timestamp))
}

export {
  getIsoDayInTimezone,
  formatTimeInTimezone,
  formatLongDateInTimezone,
  formatShortDateInTimezone,
}
